import type { Material, Variant } from '@/types/batch';
import type { VariantSelection } from '@/types/respondent';

/**
 * Returns the first material whose associated survey has not yet been completed.
 *
 * @param materials - The list of materials in the batch.
 * @param completedSurveyIds - The set of UUIDs for the completed surveys.
 * @returns The first uncompleted material, or null if all materials are completed.
 */
export function getFirstUncompletedMaterial(
  materials: Material[],
  completedSurveyIds: Set<string>
): Material | null {
  return materials.find((m: Material) => {
    const isBaseSurveyCompleted = completedSurveyIds.has(m.survey);
    const isVariantSurveyCompleted = m.variant ? completedSurveyIds.has(m.variant.survey) : false;
    return !isBaseSurveyCompleted && !isVariantSurveyCompleted;
  }) ?? null;
}

/**
 * Determine the estimated completion time for a material, taking any selected variant into account.
 *
 * @param material - The material being administered.
 * @param variantSelections - The respondent's variant selections.
 * @returns The estimated number of minutes required to complete the selected material or variant.
 */
export function getSelectedMaterialMinutes(
  material: Material,
  variantSelections: VariantSelection[]
): number {
  const variant: Variant | null = material.variant;

  if (!variant) return material.minutes;

  const selection: VariantSelection | undefined = variantSelections.find(
    (selection) => selection.variant === variant.id
  );
  const isSelected: boolean = selection?.isSelected ?? variant.isDefault;

  return isSelected ? variant.minutes : material.minutes;
}

/**
 * Determine whether a Material has been completed. A material is considered completed if either its main survey
 * or its variant survey has been completed.
 *
 * @param material - The material to check.
 * @param completedSurveyIds - Set of UUIDs for surveys that have been completed.
 * @returns True if the material has been completed; otherwise, false.
 */
export function isMaterialCompleted(
  material: Material,
  completedSurveyIds: Set<string>
): boolean {
  return (
    completedSurveyIds.has(material.survey) ||
    Boolean(
      material.variant &&
      completedSurveyIds.has(material.variant.survey)
    )
  );
}